import {
  Position,
  PricingLineItem,
  WindowOrder,
} from '@cutr/constants/windows/windows';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useRevalidator } from 'react-router-dom';

import { hashPosition, usePositionStore } from '@/windows/api/positionStore';
import { windowApi } from '@/windows/queries/windowApi';

import { keys as orderKeys } from '../order/keys';
import { keys } from './keys';
import { useHydratePositionStore } from './mappers';

const updateCache = (
  queryClient: QueryClient,
  orderId: string,
  positionId: string | null, // non-null means update or delete
  updatedPosition: Position | null, // null means deletion
  action: 'add' | 'update' | 'delete',
  updatedPricing: PricingLineItem[]
) => {
  const updatedPositions = (positions: Position[]) => {
    const existing = positions || [];

    switch (action) {
      case 'add':
        return [...existing, updatedPosition!];
      case 'update':
        return existing.map((p) =>
          p.id === updatedPosition!.id ? updatedPosition! : p
        );
      case 'delete':
        return existing.filter((p) => p.id !== positionId);
    }
  };
  // Update positions and pricing line items on order
  queryClient.setQueryData<WindowOrder>(orderKeys.getOne(orderId), (old) => {
    if (!old) return old;

    return {
      ...old,
      positions: updatedPositions(old.positions),
      pricingLineItems: updatedPricing,
    };
  });

  // Update positions-only get-all positions query if needed
  queryClient.setQueryData<Position[]>(keys.getAll(orderId), (old) => {
    if (!old) return old;

    return updatedPositions(old);
  });

  // Update position-specific query
  if (updatedPosition) {
    queryClient.setQueryData<Position>(
      keys.getOne(updatedPosition!.id),
      updatedPosition
    );
  } else {
    if (!positionId) {
      throw new Error('positionId must be provided to delete a position');
    }
    queryClient.removeQueries({
      queryKey: keys.getOne(positionId),
    });
  }
};

const useCreatePosition = () => {
  const queryClient = useQueryClient();
  const { orderId } = useParams();

  if (!orderId) {
    throw new Error('orderId must be in the URL to create a position');
  }

  return useMutation({
    mutationFn: ({ title }: { title: string }) =>
      windowApi.createPosition(orderId, { title }),
    mutationKey: keys.create(orderId),
    onSuccess: async (data) => {
      updateCache(
        queryClient,
        orderId,
        null,
        data.position,
        'add',
        data.orderPricingLineItems
      );
    },
  });
};

export const useNewPosition = () => {
  const { t } = useTranslation();
  const { mutateAsync: createPosition, isPending: isCreatingPosition } =
    useCreatePosition();
  const navigate = useNavigate();
  const { orderId } = useParams();
  const hydrateStore = useHydratePositionStore();

  if (!orderId) {
    throw new Error('orderId must be in the URL to create a position');
  }
  const { data: positions } = useQuery(getPositionsQuery(orderId));

  const onNewPosition = React.useCallback(async () => {
    if (isCreatingPosition) {
      return;
    }

    const title = generatePositionTitle({
      positions: positions || [],
      t,
    });
    const { position } = await createPosition({ title });
    hydrateStore(position);
    navigate(`/windows/orders/${orderId}/configuration/${position.id}`);
    return position;
  }, [
    createPosition,
    isCreatingPosition,
    navigate,
    orderId,
    hydrateStore,
    positions,
    t,
  ]);

  return { onNewPosition };
};

const generatePositionTitle = ({
  positions,
  t,
}: {
  positions: Position[];
  t: ReturnType<typeof useTranslation>['t'];
}) => {
  const defaultTitle = t('reseller.positionTitle');
  if (positions.length === 0) {
    return defaultTitle;
  }

  const nextNumber =
    positions
      .map((p) => p.title)
      .filter((t) => t.startsWith(defaultTitle))
      .map((t) => t.replace(defaultTitle, ''))
      .map((t) => {
        if (t === '') {
          return 1;
        }
        return Number(t);
      })
      .filter((n) => Number.isInteger(n))
      .reduce((acc, num) => Math.max(acc, num), 0) + 1;

  return nextNumber === 1 ? defaultTitle : `${defaultTitle} ${nextNumber}`;
};

export const useDuplicatePosition = () => {
  const queryClient = useQueryClient();
  const { orderId, positionId } = useParams();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  if (!orderId || !positionId) {
    throw new Error(
      'orderId and positionId must be in the URL to duplicate a position'
    );
  }

  const { mutateAsync: duplicatePosition, isPending: isDuplicatingPosition } =
    useMutation({
      mutationFn: ({ title }: { title: string }) =>
        windowApi.duplicatePosition(orderId, positionId, { title }),
      mutationKey: keys.duplicate(positionId),
      onSuccess: async (data) => {
        updateCache(
          queryClient,
          orderId,
          null,
          data.position,
          'add',
          data.orderPricingLineItems
        );
        revalidator.revalidate();
      },
    });
  const { t } = useTranslation();
  const { data: positions } = useQuery(getPositionsQuery(orderId));

  const onDuplicatePosition = React.useCallback(async () => {
    if (isDuplicatingPosition) {
      return;
    }

    const title = generatePositionTitle({
      positions: positions || [],
      t,
    });
    const { position } = await duplicatePosition({ title });
    navigate(`/windows/orders/${orderId}/configuration/${position.id}`);
    return position;
  }, [
    duplicatePosition,
    isDuplicatingPosition,
    navigate,
    orderId,
    positions,
    t,
  ]);

  return { onDuplicatePosition };
};

export const getPositionQuery = (orderId: string, positionId: string) => ({
  queryKey: keys.getOne(positionId),
  queryFn: () => windowApi.getPosition(orderId, positionId),
});

export const getPositionsQuery = (orderId: string) => ({
  queryKey: keys.getAll(orderId),
  queryFn: () => windowApi.getPositions(orderId),
});

export const useUpdatePosition = () => {
  const queryClient = useQueryClient();
  const { setHash } = usePositionStore();
  const { orderId, positionId } = useParams();
  const revalidator = useRevalidator();

  if (!orderId || !positionId) {
    throw new Error(
      'orderId and positionId must be in the URL to update a position'
    );
  }

  return useMutation({
    mutationFn: ({ data }: { data: Partial<Position> }) =>
      windowApi.patchPosition(orderId, positionId, data),
    mutationKey: keys.update(positionId),
    onSuccess: async (data) => {
      updateCache(
        queryClient,
        orderId,
        positionId,
        data.position,
        'update',
        data.orderPricingLineItems
      );
      setHash(hashPosition(data.position));
      revalidator.revalidate();
    },
  });
};

export const useDeletePosition = () => {
  const queryClient = useQueryClient();
  const { orderId, positionId } = useParams();
  const navigate = useNavigate();
  const revalidator = useRevalidator();

  if (!orderId || !positionId) {
    throw new Error(
      'orderId and positionId must be in the URL to delete a position'
    );
  }

  const { mutateAsync: deletePosition, isPending: isDeletingPosition } =
    useMutation({
      mutationFn: () => windowApi.deletePosition(orderId, positionId),
      mutationKey: keys.delete(positionId),
      onSuccess: async (data) => {
        updateCache(
          queryClient,
          orderId,
          positionId,
          null,
          'delete',
          data.orderPricingLineItems
        );
        revalidator.revalidate();
      },
    });

  const onDeletePosition = React.useCallback(async () => {
    if (isDeletingPosition) {
      return;
    }
    await deletePosition();
    const positions = queryClient.getQueryData<Position[]>(
      keys.getAll(orderId)
    );
    if (positions) {
      const lastPosition = positions[positions.length - 1] as Position;
      if (lastPosition) {
        navigate(`/windows/orders/${orderId}/configuration/${lastPosition.id}`);
        return;
      }
    }
    navigate(`/windows/orders/${orderId}/configuration`);
  }, [deletePosition, isDeletingPosition, navigate, orderId, queryClient]);

  return { onDeletePosition };
};
